import { AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import dayjs from "dayjs";
import React from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import Charting from "../../../Components/Charting";
import Constants from "../../../Constants";
import { usdToUoI } from "../../../Constants/billing";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import utils from "../../Common/utils";
import DashboardItem from "./DashboardItem";
import DashboardPlaceholder from "./DashboardPlaceholder";

const CostingView = ({
  consumptionAPIInProgress,
  costAPIInProgress,
  datasets,
  selectedDate,
  changeMonthByUnit,
  totalPredictionCost,
  totalTrainingCost,
  costChart,
  normalisedConsumption,
  normalisedTrainingConsumption,
  displayedAgents,
  consumptionByType,
  consumptionByModel,
  totalUoIConsumed,
  cacheUsage,
  billingPeriod,
}) => {
  const { t, i18n } = useTranslation();
  const isCurrentMonth = dayjs().month() === selectedDate.month();
  dayjs.locale(i18n.language);
  const { currentTheme } = AppTheme.useTheme();
  const {
    mapModeWithLabel,
    isWorkspaceOnDemand,
    getPlanConfig,
    getUoiBudget,
  } = useWorkspaces();
  const isWorkspaceBaseConsumption = isWorkspaceOnDemand();
  const isParseCost =
    normalisedConsumption?.datasets?.length > 0 || datasets?.length > 0;
  function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (let i = 0; i < 3; i++) {
      let value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).substr(-2);
    }
    return color;
  }
  const _formatNumber = (num) => {
    return (num || 0).toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  const _mappedLabel = (label) => {
    const _agent = displayedAgents?.find((u) => u?.id == label);
    // change here
    return `${_agent?.label || t("dashboard.costingView.notAvailable")} - ${
      mapModeWithLabel(_agent?.mode) || t("dashboard.costingView.unknown")
    }`;
  };

  const _mapWorkflowTypeLabel = (key) => {
    const labelMap = {
      chatter: t("dashboard.costingView.chatter"),
      embedder: t("dashboard.costingView.embedder"),
      planner: t("dashboard.costingView.planner"),
      generator: t("dashboard.costingView.generator"),
      analyser: t("dashboard.costingView.analyser"),
      browser: t("dashboard.costingView.browser"),
      browserAndProxy: t("dashboard.costingView.browserAndProxy"),
      knowledgeHubIndexing: t("dashboard.costingView.knowledgeHubIndexing"),
      code_execution: t("dashboard.costingView.codeExecution"),
    };
    return labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  const WORKFLOW_TYPE_GROUPS = {
    browserAndProxy: ["browser", "proxy"],
    knowledgeHubIndexing: ["embedder", "vectorisation", "image_extraction"],
  };

  const _groupConsumptionByType = (consumptionByType) => {
    const grouped = {};
    Object.entries(consumptionByType || {}).forEach(([key, value]) => {
      const groupKey = Object.keys(WORKFLOW_TYPE_GROUPS).find((group) =>
        WORKFLOW_TYPE_GROUPS[group].includes(key),
      );
      const targetKey = groupKey || key;
      if (!grouped[targetKey]) grouped[targetKey] = { uoi: 0 };
      grouped[targetKey].uoi += value?.uoi || 0;
    });
    return grouped;
  };

  const groupedConsumptionByType = _groupConsumptionByType(consumptionByType);

  const _mapModelSizeLabel = (key) => {
    const labelMap = {
      ultra: t("dashboard.costingView.modelUltra"),
      extraLarge: t("dashboard.costingView.modelExtraLarge"),
      large: t("dashboard.costingView.modelLarge"),
      medium: t("dashboard.costingView.modelMedium"),
      small: t("dashboard.costingView.modelSmall"),
      extraSmall: t("dashboard.costingView.modelExtraSmall"),
      local: t("dashboard.costingView.modelLocal"),
      custom: t("dashboard.costingView.modelCustom"),
      external: t("dashboard.costingView.modelExternal"),
      unspecified: t("dashboard.costingView.modelUnspecified"),
    };
    return labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  // Model-size breakdown sorted high→low, with each share of total UoI.
  const modelBreakdown = React.useMemo(() => {
    const entries = Object.entries(consumptionByModel || {}).filter(
      ([, v]) => v > 0,
    );
    const total = entries.reduce((acc, [, v]) => acc + v, 0) || 1;
    return entries
      .sort((a, b) => b[1] - a[1])
      .map(([key, uoi]) => ({
        key,
        uoi,
        percentage: (uoi / total) * 100,
      }));
  }, [consumptionByModel]);

  // Ensure small values have minimum visibility in pie chart
  // minPercentage: minimum percentage of total to display (default 3%)
  const _getAdjustedChartData = (data, minPercentage = 3) => {
    if (!data || data.length === 0) return { adjusted: [], original: [] };

    const total = data.reduce((sum, val) => sum + (val || 0), 0);
    if (total === 0) return { adjusted: data, original: data };

    const minValue = (total * minPercentage) / 100;

    const adjusted = data.map((val) => {
      if (val > 0 && val < minValue) {
        return minValue;
      }
      return val || 0;
    });

    return { adjusted, original: data };
  };

  return (
    <View
      style={{
        gap: 5,
      }}
    >
      <AppText
        wrapperStyle={{
          width: "fit-content",
          alignSelf: "flex-end",
          paddingTop: 10,
          zIndex: 10,
        }}
        leftIcon={AppAssets.icons.navigationBack}
        rightIcon={AppAssets.icons.navigationTo}
        onRightIconPress={() => {
          if (!isCurrentMonth) changeMonthByUnit(1);
        }}
        rightIconColor={
          isCurrentMonth
            ? currentTheme?.theme?.grey
            : currentTheme?.theme?.primary
        }
        onLeftIconPress={() => {
          changeMonthByUnit(-1);
        }}
      >
        {billingPeriod
          ? `${dayjs(billingPeriod.billingPeriodStart).format(
              "DD MMM",
            )} - ${dayjs(billingPeriod.billingPeriodEnd).format("DD MMM")}`
          : utils.capitalizeFirstLetter(selectedDate?.format("MMM YY"))}
      </AppText>
      <DashboardItem
        isFetchInProgress={consumptionAPIInProgress}
        title={t("dashboard.costingView.uoiConsumptionByAgent")}
        isHalfWidth={false}
      >
        {isParseCost && normalisedConsumption?.datasets?.length > 0 ? (
          <Charting.AppBarChart
            maxHeight={Constants.chartMaxHeight}
            options={{
              legend: {
                position: "bottom",
                display: true,
                labels: {
                  color: currentTheme?.theme?.font_color,
                  font: {
                    family: "normalFont",
                    size: currentTheme?.theme?.extra_small_font_size,
                  },
                },
              },
              plugins: {
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      return `${context.dataset.label}: ${_formatNumber(
                        context.parsed.y,
                      )} ${t("dashboard.costingView.uoi")}`;
                    },
                  },
                },
              },
              scales: {
                x: {
                  stacked: true,
                  grid: {
                    display: false,
                  },
                  ticks: {
                    color: currentTheme?.theme?.font_color,
                    font: {
                      size: currentTheme?.theme?.extra_small_font_size,
                    },
                  },
                },
                y: {
                  type: "linear",
                  stacked: true,
                  display: true,
                  position: "left",
                  ticks: {
                    color: currentTheme?.theme?.font_color,
                    font: {
                      size: currentTheme?.theme?.extra_small_font_size,
                    },
                  },
                },
              },
            }}
            dataset={{
              labels: normalisedConsumption?.labels,
              datasets: [
                ...normalisedConsumption?.datasets
                  ?.map((u) => {
                    {
                      const hasConsumption = u.data?.some((value) => value > 0);
                      if (
                        hasConsumption &&
                        displayedAgents?.map((u) => u?.id).includes(u.label)
                      )
                        return {
                          label: _mappedLabel(u.label),
                          data: u.data,
                          borderColor: stringToColor(u.label),
                          borderRadius: 10,
                          backgroundColor: stringToColor(u.label),
                          yAxisID: "y",
                        };
                    }
                  })
                  ?.filter((u) => u),
                {
                  type: "line",
                  label: t("dashboard.costingView.totalUoI"),
                  data: normalisedConsumption?.labels?.map(
                    (_, dateIndex) =>
                      normalisedConsumption?.datasets
                        ?.filter(
                          (u) =>
                            u.data?.some((v) => v > 0) &&
                            displayedAgents
                              ?.map((a) => a?.id)
                              .includes(u.label),
                        )
                        ?.reduce(
                          (sum, u) => sum + (u.data?.[dateIndex] || 0),
                          0,
                        ) || 0,
                  ),
                  borderColor: currentTheme?.theme?.font_color,
                  backgroundColor: currentTheme?.theme?.font_color,
                  borderWidth: 2,
                  pointRadius: 3,
                  fill: false,
                  tension: 0.3,
                  yAxisID: "y",
                },
              ],
            }}
          />
        ) : (
          <DashboardPlaceholder
            mainCaption={t("dashboard.costingView.emptyPlaceholder")}
            subCaption={t("dashboard.costingView.runFirstRequest")}
          />
        )}
      </DashboardItem>
      {/* Row with UoI pie chart and Training consumption */}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        {/* UoI Consumption by Workflow Type - 50% width */}
        <DashboardItem
          isFetchInProgress={consumptionAPIInProgress}
          title={t("dashboard.costingView.uoiConsumptionByType")}
          isHalfWidth={false}
          customWidth="50%"
          additionalTitle={` ${_formatNumber(
            totalUoIConsumed,
          )} / ${_formatNumber(getUoiBudget() || 0)} ${t(
            "dashboard.costingView.uoi",
          )}${
            cacheUsage?.cacheSavingsUSD > 0
              ? ` · ${t("dashboard.costingView.cacheSavings")}: ${_formatNumber(
                  usdToUoI(cacheUsage.cacheSavingsUSD),
                )} ${t("dashboard.costingView.uoi")}${
                  cacheUsage.cacheHitRate > 0
                    ? ` (${Math.round(cacheUsage.cacheHitRate * 100)}%)`
                    : ""
                }`
              : ""
          }`}
          dashboardTitleWrapperStyle={{
            flexDirection: "column",
          }}
          mainTitleWrapperStyle={{
            paddingBottom: 5,
          }}
          additionalTitleWrapperStyle={{
            paddingTop: 0,
            paddingLeft: 15,
          }}
        >
          {groupedConsumptionByType &&
          Object.keys(groupedConsumptionByType).length > 0 &&
          Object.values(groupedConsumptionByType).some((v) => v?.uoi > 0) ? (
            (() => {
              const budget = getUoiBudget() || 1;
              const consumed = totalUoIConsumed || 0;
              const percentage = ((consumed / budget) * 100).toFixed(1);
              const originalUoiValues = Object.values(
                groupedConsumptionByType,
              ).map((v) => v?.uoi || 0);
              const { adjusted: adjustedUoiValues } = _getAdjustedChartData(
                originalUoiValues,
              );
              return (
                <View
                  style={{
                    width: "90%",
                  }}
                >
                  <Charting.AppDoughnut
                    maxHeight={Constants.doughnutChartMaxHeight}
                    options={{
                      plugins: {
                        legend: {
                          position: "bottom",
                          labels: {
                            color: currentTheme?.theme?.font_color,
                            font: {
                              family: "normalFont",
                              size: currentTheme?.theme?.extra_small_font_size,
                            },
                          },
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const index = context.dataIndex;
                              const actualValue = originalUoiValues[index];
                              return ` ${_formatNumber(actualValue)} ${t(
                                "dashboard.costingView.uoi",
                              )}`;
                            },
                          },
                        },
                      },
                    }}
                    dataset={{
                      labels: Object.keys(groupedConsumptionByType).map((key) =>
                        _mapWorkflowTypeLabel(key),
                      ),
                      datasets: [
                        {
                          label: "UoI",
                          data: adjustedUoiValues,
                          backgroundColor: Object.keys(
                            groupedConsumptionByType,
                          ).map((key) => stringToColor(key)),
                        },
                      ],
                    }}
                  />
                  <View style={{ alignItems: "center", marginTop: 10 }}>
                    <AppText
                      style={{
                        fontSize: currentTheme?.theme?.semi_small_font_size,
                        color: currentTheme?.theme?.font_color,
                      }}
                    >
                      {t("dashboard.costingView.budgetUsed")}
                    </AppText>
                    <AppText
                      preventTranslation
                      fontWeight="600"
                      style={{
                        fontSize: currentTheme?.theme?.large_font_size,
                        color:
                          percentage > 90
                            ? currentTheme?.theme?.red
                            : percentage > 70
                            ? currentTheme?.theme?.orange
                            : currentTheme?.theme?.green,
                      }}
                    >
                      {`${percentage}%`}
                    </AppText>
                  </View>
                </View>
              );
            })()
          ) : (
            <DashboardPlaceholder
              mainCaption={t("dashboard.modelView.nothingToShow")}
              subCaption={t("dashboard.costingView.runFirstRequestTracking")}
            />
          )}
        </DashboardItem>

        {/* Training consumption - 48% width */}
        {/* UoI by model size - 48% width (replaces the training bar chart) */}
        <DashboardItem
          isFetchInProgress={consumptionAPIInProgress}
          title={t("dashboard.costingView.uoiByModel")}
          isHalfWidth={false}
          customWidth="48%"
          dashboardTitleWrapperStyle={{
            flexDirection: "column",
          }}
          mainTitleWrapperStyle={{
            paddingBottom: 5,
          }}
        >
          {modelBreakdown?.length > 0 ? (
            <View style={{ width: "90%" }}>
              <Charting.AppDoughnut
                maxHeight={Constants.doughnutChartMaxHeight}
                options={{
                  plugins: {
                    legend: {
                      position: "bottom",
                      labels: {
                        color: currentTheme?.theme?.font_color,
                        font: {
                          family: "normalFont",
                          size: currentTheme?.theme?.extra_small_font_size,
                        },
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const entry = modelBreakdown[context.dataIndex];
                          return ` ${_formatNumber(entry?.uoi)} ${t(
                            "dashboard.costingView.uoi",
                          )} (${entry?.percentage.toFixed(1)}%)`;
                        },
                      },
                    },
                  },
                }}
                dataset={{
                  labels: modelBreakdown.map((m) =>
                    _mapModelSizeLabel(m.key),
                  ),
                  datasets: [
                    {
                      label: t("dashboard.costingView.uoi"),
                      data: modelBreakdown.map((m) => m.uoi),
                      backgroundColor: modelBreakdown.map((m) =>
                        stringToColor(m.key),
                      ),
                    },
                  ],
                }}
              />
              <View style={{ marginTop: 8, paddingHorizontal: 4 }}>
                {modelBreakdown.map((m) => (
                  <View
                    key={m.key}
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      paddingVertical: 2,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        flex: 1,
                      }}
                    >
                      <View
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          backgroundColor: stringToColor(m.key),
                          marginRight: 8,
                        }}
                      />
                      <AppText
                        style={{
                          fontSize:
                            currentTheme?.theme?.extra_small_font_size,
                          color: currentTheme?.theme?.font_color,
                        }}
                      >
                        {_mapModelSizeLabel(m.key)}
                      </AppText>
                    </View>
                    <AppText
                      preventTranslation
                      style={{
                        fontSize: currentTheme?.theme?.extra_small_font_size,
                        color: currentTheme?.theme?.softGrey,
                      }}
                    >
                      {`${m.percentage.toFixed(1)}%`}
                    </AppText>
                  </View>
                ))}
              </View>
            </View>
          ) : (
            <DashboardPlaceholder
              mainCaption={t("dashboard.modelView.nothingToShow")}
              subCaption={t("dashboard.costingView.runFirstRequestTracking")}
            />
          )}
        </DashboardItem>
      </View>

      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          marginVertical: 1,
        }}
      >
        {isWorkspaceBaseConsumption && Constants.showCost && (
          <>
            <DashboardItem
              isFetchInProgress={costAPIInProgress}
              title={t("dashboard.costingView.costPrediction")}
              isHalfWidth={true}
              additionalTitle={` ${_formatNumber(totalPredictionCost)}$`}
            >
              {costChart?._parsingCost &&
              costChart?._parsingCost?.reduce((a, b) => a + b, 0) > 0 ? (
                <Charting.AppDoughnut
                  maxHeight={Constants.doughnutChartMaxHeight}
                  options={{
                    plugins: {
                      tooltip: {
                        callbacks: {
                          label: (context) => {
                            return ` ${_formatNumber(context.parsed)} $`;
                          },
                        },
                      },
                    },
                  }}
                  dataset={{
                    labels: costChart?._parsingLabels.map((label) =>
                      _mapMethodTypeLabel(label),
                    ),
                    datasets: [
                      {
                        label: t("dashboard.costingView.sentences"),
                        data: costChart?._parsingCost,
                        backgroundColor: costChart?._parsingLabels.map((u) =>
                          stringToColor(u),
                        ),
                      },
                    ],
                  }}
                />
              ) : (
                <DashboardPlaceholder
                  mainCaption={t("dashboard.modelView.nothingToShow")}
                  subCaption={t(
                    "dashboard.costingView.runFirstRequestTracking",
                  )}
                />
              )}
            </DashboardItem>
          </>
        )}
      </View>
    </View>
  );
};

export default CostingView;
